// ─── PDKS (Personel Devam Kontrol Sistemi) MODELLERİ ──────────────────────────

export interface IGirisCikis {
  _id: string;
  firmaId: string;
  personelId: string;
  cihazId: string;
  kartUid: string;
  tur: GirisCikisTur;
  zaman: string;
  konum?: string;
  onayDurumu: OnayDurumu;
  notlar?: string;
  /** Offline senkronizasyonda aynı hareketin iki kez yazılmasını engeller. */
  istemciKayitId?: string;
  // $lookup
  personel?: any;
  cihaz?: any;
}

// mola_baslangic / mola_bitis: HMI panelinden mola giriş/çıkış kaydı (hmi.mola yetkisi gerektirir)
export type GirisCikisTur = 'giris' | 'cikis' | 'mola_baslangic' | 'mola_bitis';
// duzeltme: hmi.puantaj_duzenle veya puantaj.duzenle yetkili kullanici tarafindan yapilan manuel duzeltme
export type OnayDurumu = 'otomatik' | 'manuel' | 'duzeltme';

// ─── PUANTAJ ──────────────────────────────────────────────────────────────────

export interface IPuantajSatir {
  personelId: string;
  personelAd: string;
  personelSoyad: string;
  sicilNo: string;
  departmanAdi?: string;
  hareketler: IGirisCikisHareket[];
  toplamCalisma: number;   // dakika
  mesaiSuresi: number;     // dakika
  gecGelme: number;        // dakika
  erkenCikis: number;      // dakika
  durum: PuantajDurum;
}

export interface IGirisCikisHareket {
  tur: GirisCikisTur;
  zaman: string;
  cihazId: string;
}

export type PuantajDurum =
  | 'normal'
  | 'gec_geldi'
  | 'erken_cikti'
  | 'devamsiz'
  | 'izinli'
  | 'mesaili';

// ─── ÜRETİM KAYDI ────────────────────────────────────────────────────────────

export interface IUretimKayit {
  _id: string;
  firmaId: string;
  cihazId: string;
  personelId: string;
  isEmriNo?: string;
  urunKodu?: string;
  miktar: number;
  birim: string;
  durum: UretimDurum;
  baslangicZamani: string;
  bitisZamani?: string;
  notlar?: string;
}

export type UretimDurum = 'basladi' | 'devam' | 'tamamlandi' | 'durduruldu';

// ─── ZİYARETÇİ ───────────────────────────────────────────────────────────────

export interface IZiyaretci {
  _id: string;
  firmaId: string;
  cihazId: string;
  adSoyad: string;
  tcKimlik?: string;
  sirket?: string;
  ziyaretNedeni: string;
  ziyaretEdilenPersonelId?: string;
  girisZamani: string;
  cikisZamani?: string;
  fotoUrl?: string;
  geciciKartUid?: string;
}

// ─── PDKS AYARLARI (firma bazında özelleştirilebilir) ────────────────────────

export interface IPdksAyarlar {
  firmaId: string;

  // Mesai çarpanları (firma bazında custom tanımlanabilir)
  mesaiCarpanlari: IMesaiCarpani[];

  // Tolerans ayarları
  girisToleransDk: number;       // geç gelme toleransı (dk)
  cikisToleransDk: number;       // erken çıkış toleransı (dk)

  // Haftalık çalışma ayarları
  haftalikCalismaSaati: number;  // haftalık normal çalışma saati (ör: 45)
  gunlukCalismaSaati: number;    // günlük normal çalışma saati (ör: 9)

  // Mola ayarları
  molaToplam: number;            // günlük toplam mola hakkı (dk)
  molaAsimDusulmeSinMi: boolean; // mola aşımı mesaiden düşülsün mü

  // Çift kart okutma engeli
  kartOkutmaAralikSn: number;    // aynı kart tekrar okutma aralığı (saniye, ör: 120)

  // Gece vardiyası
  geceVardiyasiGunAtamaSaati: number; // bu saatten sonra giriş → ertesi günün vardiyası (ör: 20)

  // Birden fazla giriş/çıkış
  cokluGirisCikis: boolean;      // bir güne birden fazla giriş/çıkış kaydedilebilir mi

  // Resmi tatiller
  resmiTatiller: IResmiTatil[];

  // Part-time çalışma günleri (0=Paz, 6=Cmt)
  varsayilanCalismaGunleri: number[]; // ör: [1,2,3,4,5] = Pzt-Cum
}

export interface IMesaiCarpani {
  kod: string;           // benzersiz kod (ör: 'hafta_ici', 'cumartesi', 'pazar', 'resmi_tatil', 'gece')
  etiket: string;        // gösterim ismi (ör: 'Cumartesi Mesaisi')
  carpan: number;        // çarpan (ör: 1.5 = %150)
  renk?: string;         // UI renk kodu
  otomatikUygula: boolean; // otomatik uygulanacak mı (yoksa manuel seçim)
  uygulamaKosulu?: MesaiKosul; // otomatik koşul
}

export type MesaiKosul =
  | 'hafta_ici'        // Pzt-Cum
  | 'cumartesi'        // Cmt
  | 'pazar'            // Paz
  | 'resmi_tatil'      // resmi tatil gününde
  | 'gece_vardiyasi'   // gece vardiyasında
  | 'hafta_sonu'       // Cmt+Paz
  | 'her_zaman';       // her zaman uygula

export interface IResmiTatil {
  tarih: string;        // YYYY-MM-DD
  isim: string;
  yarimGun?: boolean;   // yarım gün mü
}

// ─── AYLIK ÖZET ─────────────────────────────────────────────────────────────

export interface IAylikOzet {
  gunler: IAylikGunOzet[];
  toplamNormalDk: number;
  toplamFazlaMesaiDk: number;
  toplamMolaDk: number;
  devamsizGunSayisi: number;
  izinliGunSayisi: number;
  raporluGunSayisi: number;
  calismaGunSayisi: number;
  tatilGunSayisi: number;
}

export interface IAylikGunOzet {
  tarih: string;            // YYYY-MM-DD
  kayitlar: IGunlukKayit[]; // birden fazla giriş/çıkış
  toplamCalismaDk: number;
  toplamMolaDk: number;
  fazlaMesaiDk: number;
  gecGelisDk: number;
  erkenCikisDk: number;
  mesaiCarpani: number;     // uygulanan çarpan
  mesaiCarpanKodu?: string; // hangi çarpan kuralı
  durum: PuantajGunDurum;
  bayraklar: string[];      // ['tatil', 'rapor', 'izin', 'servis' ...]
}

export type PuantajGunDurum =
  | 'normal'           // normal çalışma
  | 'gec_geldi'        // tolerans aşımı
  | 'erken_cikti'      // erken çıkış
  | 'devamsiz'         // gelmedi
  | 'izinli'           // izinli
  | 'raporlu'          // raporlu
  | 'tatil'            // resmi tatil
  | 'calisma_gunu_degil' // part-time: çalışma günü değil
  | 'mesaili'          // fazla mesai var
  | 'gece_vardiyasi';  // gece vardiyası kaydı

export interface IGunlukKayit {
  giris?: string;       // ISO zaman
  cikis?: string;       // ISO zaman
  molalar: IMolaKayit[];
  calismaDk: number;
  molaDk: number;
  durum: 'aktif' | 'cikti' | 'molada' | 'gelmedi';
}

export interface IMolaKayit {
  baslangic: string;
  bitis?: string;
  sureDk: number;
  ismi?: string;
}

// ─── OFFLİNE KAYIT ─────────────────────────────────────────────────────────

export interface IOfflineKayit {
  id: string;           // client-generated unique ID
  firmaId: string;
  personelId: string;
  kartUid: string;
  cihazId: string;
  tur: GirisCikisTur;
  zaman: string;
  senkronize: boolean;
  hataKodu?: string;
  denemeSayisi: number;
}

// ─── VARSAYILAN PDKS AYARLARI ───────────────────────────────────────────────

export const VARSAYILAN_PDKS_AYARLAR: Omit<IPdksAyarlar, 'firmaId'> = {
  mesaiCarpanlari: [
    { kod: 'hafta_ici', etiket: 'Hafta İçi Normal', carpan: 1.0, renk: '#52c41a', otomatikUygula: true, uygulamaKosulu: 'hafta_ici' },
    { kod: 'cumartesi', etiket: 'Cumartesi Mesaisi', carpan: 1.5, renk: '#faad14', otomatikUygula: true, uygulamaKosulu: 'cumartesi' },
    { kod: 'pazar', etiket: 'Pazar Mesaisi', carpan: 2.0, renk: '#ff4d4f', otomatikUygula: true, uygulamaKosulu: 'pazar' },
    { kod: 'resmi_tatil', etiket: 'Resmi Tatil Mesaisi', carpan: 2.0, renk: '#722ed1', otomatikUygula: true, uygulamaKosulu: 'resmi_tatil' },
    { kod: 'gece', etiket: 'Gece Vardiyası', carpan: 1.5, renk: '#1890ff', otomatikUygula: true, uygulamaKosulu: 'gece_vardiyasi' },
  ],
  girisToleransDk: 10,
  cikisToleransDk: 5,
  haftalikCalismaSaati: 45,
  gunlukCalismaSaati: 9,
  molaToplam: 60,
  molaAsimDusulmeSinMi: true,
  kartOkutmaAralikSn: 120,
  geceVardiyasiGunAtamaSaati: 20,
  cokluGirisCikis: true,
  resmiTatiller: [],
  varsayilanCalismaGunleri: [1, 2, 3, 4, 5], // Pzt-Cum
};

// ─── DENETİM KAYDI ───────────────────────────────────────────────────────────

export interface IDenetimKaydi {
  _id: string;
  firmaId: string;
  islem: string;
  modul: string;
  detay: Record<string, any>;
  kullaniciId: string;
  tarih: string;
  ipAdresi?: string;
}
